import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * AI cost governance dashboard surface (T-0112 / C20-122, T-0014).
 *
 * Wires the firm-admin AI-cost dashboard to the real composite endpoint
 * (PR #133):
 *
 *   GET /api/v1/admin/ai-costs/summary
 *     → {
 *         firm_cap_cents,
 *         spend_total_last_12_months_cents,
 *         spend_trend_monthly[]   (month + by_work_class + total),
 *         by_work_class[]         (calls/avg_tokens/cost_per_call/total),
 *         by_engagement[]         (engagement metadata + spend + alerts),
 *         recent_expensive_calls[],
 *         firm_used_ratio
 *       }
 *
 * UI ↔ backend shape mapping
 * --------------------------
 * The screen was scaffolded against a richer mock shape that carries
 * model-pinning metadata, engagement display names, work-class
 * presentation strings, and billing-period day counts — most of which
 * are NOT in the backend's composite (T-0014 deferred pinning to App
 * Configuration; engagement name resolution rides per-row from the
 * backend). To keep the existing card layout working without a UI
 * rewrite, this module:
 *
 *   1. Reads the backend composite via apiClient.
 *   2. Hydrates each row into the UI's existing `AiCostSummary` shape
 *      via `hydrateSummary()` — derives missing fields (billing-period
 *      days, per-work-class labels) from sensible defaults (current
 *      calendar month for period bounds). The model deployments come from
 *      `GET /api/v1/admin/ai-costs/model` (`useAiModelQuery`), which reads
 *      the real deployment names (MODEL_CHAT, EMBEDDING_MODEL) and says
 *      whether each has a rate-card row.
 *   3. Maps the backend's 4 work classes (baseline / routing / reeval /
 *      chat) onto the UI's 6-slot table. Slots without a backend
 *      counterpart (narrative / embed) render with zero counts so the
 *      visualisation stays stable even when those work classes haven't
 *      run yet.
 *
 * T-0154 — firm scope. AI work with no engagement to bill is billed to the
 * firm scope, against a firm monthly cap, under its own work class:
 * `library_intake` (requirement library document intake and its
 * embeddings) or `vendor_knowledge` (the vendor knowledge base, T-0193):
 *
 *   summary.firm_scope                      this month's firm-scope spend
 *   GET / PUT /api/v1/admin/ai-costs/firm-budget   read / change the cap
 *
 * A work class is read as a plain string, never a closed enum: a class
 * this screen does not know (as `library_intake` once was) gets its own
 * row and a readable label instead of failing the whole response.
 *
 * Caps and periods are the budget gate's: each engagement row carries the
 * cap the gate enforces (its own, else the engagement default, flagged
 * `cap_is_default`) and the start of the period the gate counts spend
 * from. Spend from a model with no rate-card row was priced at the
 * fallback rate: the summary lists those models (`estimated_models`) and
 * each such call is flagged `estimated`.
 */

// ===========================================================================
// UI types — preserved so downstream components don't churn.
// ===========================================================================

export type KnownWorkClassId =
  | 'base-form'
  | 'ongoing'
  | 'reeval'
  | 'chat'
  | 'narrative'
  | 'embed'
  | 'library-intake'
  | 'vendor-knowledge';

/** A known slot, or the backend's own id for a class this screen has no slot for. */
export type WorkClassId = KnownWorkClassId | (string & {});

export interface WorkClassBreakdown {
  id: WorkClassId;
  /** Long-form name. */
  name: string;
  /** Short label. */
  label: string;
  /** Originating tickets, comma-joined. */
  tickets: string;
  calls: number;
  avg_tokens: number;
  cost_per_call_cents: number;
  total_cents: number;
}

export interface EngagementSpendRow {
  engagement_id: string;
  client_name: string;
  engagement_name: string;
  /** The cap the budget gate enforces: the engagement's own, else the engagement default. */
  cap_cents: number;
  /** True when no cap is set and the engagement default applies (older servers: false). */
  cap_is_default: boolean;
  /** ISO start of the period the gate counts spend from; null from older servers. */
  period_start: string | null;
  used_cents: number;
  txns: number;
  /** Free-form alert strings (threshold-crossed messages). */
  alerts: string[];
}

export interface RecentCallRow {
  /** ISO timestamp. */
  ts: string;
  engagement_id: string;
  client_name: string;
  work_class: WorkClassId;
  tokens: number;
  cost_cents: number;
  model_id: string;
  /** Priced at the fallback rate (no rate-card row for the model): an estimate. */
  estimated: boolean;
  /** Caller reference (e.g. correlation id). */
  ref: string;
  /** Originating user display string. */
  user: string;
}

export interface AiCostSummary {
  firm_cap_cents: number;
  firm_mtd_cents: number;
  firm_last_month_cents: number;
  /** Period start ISO date (YYYY-MM-DD). */
  billing_period_start: string;
  billing_period_days_elapsed: number;
  billing_period_days_total: number;
  /** The engagement cap applied when an engagement has none of its own; null from older servers. */
  engagement_default_cap_cents: number | null;
  /** Models whose spend was priced at the fallback rate (no rate-card row). */
  estimated_models: string[];
  per_work_class: WorkClassBreakdown[];
  per_engagement: EngagementSpendRow[];
  recent_expensive_calls: RecentCallRow[];
  /** 12-month trend, in cents, oldest first. */
  monthly_trend_cents: number[];
  /** Firm-scope spend and its cap (T-0147); null from an API that predates it. */
  firm_scope: FirmScopeSummary | null;
}

/**
 * The firm-level AI budget: a calendar-month cap with a hard stop.
 * `is_default` is true when no firm admin has set a cap; `percent_of_cap`
 * is used / cap, null when the cap is zero; `period_start` is the start of
 * the month (UTC).
 */
export type FirmAiBudget = z.infer<typeof FirmAiBudgetSchema>;

export interface FirmScopeCall {
  ts: string;
  work_class: string;
  model_id: string;
  estimated: boolean;
  tokens: number;
  cost_cents: number;
  ref: string;
}

export interface FirmScopeSummary extends FirmAiBudget {
  by_work_class: WorkClassBreakdown[];
  recent_expensive_calls: FirmScopeCall[];
}

// ===========================================================================
// Backend schema — what the composite endpoint actually returns.
// ===========================================================================

/**
 * Deliberately not an enum (T-0154): a work class added on the server must
 * never fail this response. Unknown values are labelled, not rejected.
 */
const BackendWorkClassSchema = z.string();

const BackendByWorkClassSchema = z.object({
  work_class: BackendWorkClassSchema,
  calls: z.number(),
  avg_tokens: z.number(),
  cost_per_call_cents: z.number(),
  total_cents: z.number(),
});

const BackendByEngagementSchema = z.object({
  engagement_id: z.string(),
  client_name: z.string(),
  engagement_name: z.string(),
  cap_cents: z.number(),
  cap_is_default: z.boolean().optional(),
  period_start: z.string().optional(),
  used_cents: z.number(),
  txns: z.number(),
  alerts: z.array(z.string()),
  percent_of_cap: z.number(),
});

const BackendRecentCallSchema = z.object({
  ts: z.string(),
  engagement_id: z.string(),
  client_name: z.string(),
  work_class: BackendWorkClassSchema,
  tokens: z.number(),
  cost_cents: z.number(),
  model_id: z.string().optional(),
  estimated: z.boolean().optional(),
  ref: z.string().nullable(),
  user: z.string(),
});

const BackendTrendRowSchema = z.object({
  month: z.string(),
  total_cents: z.number(),
  // Keyed by work class; the set of classes is the server's.
  by_work_class: z.record(z.number()).optional(),
});

export const FirmAiBudgetSchema = z.object({
  cap_cents: z.number(),
  is_default: z.boolean(),
  default_cap_cents: z.number(),
  used_cents: z.number(),
  remaining_cents: z.number(),
  percent_of_cap: z.number().nullable(),
  exhausted: z.boolean(),
  txns: z.number(),
  period_start: z.string(),
  work_classes: z.array(z.string()).optional(),
});

const BackendFirmScopeSchema = FirmAiBudgetSchema.extend({
  by_work_class: z.array(BackendByWorkClassSchema).optional(),
  recent_expensive_calls: z
    .array(
      z.object({
        ts: z.string(),
        work_class: BackendWorkClassSchema,
        model_id: z.string().nullable().optional(),
        estimated: z.boolean().optional(),
        tokens: z.number(),
        cost_cents: z.number(),
        ref: z.string().nullable().optional(),
      }),
    )
    .optional(),
});

const AiCostSummaryBackendSchema = z.object({
  firm_cap_cents: z.number(),
  spend_total_last_12_months_cents: z.number(),
  spend_trend_monthly: z.array(BackendTrendRowSchema),
  by_work_class: z.array(BackendByWorkClassSchema),
  by_engagement: z.array(BackendByEngagementSchema),
  recent_expensive_calls: z.array(BackendRecentCallSchema),
  firm_used_ratio: z.number(),
  firm_scope: BackendFirmScopeSchema.nullable().optional(),
  engagement_default_cap_cents: z.number().optional(),
  estimated_models: z.array(z.string()).optional(),
});

/** GET /api/v1/admin/ai-costs/model — the deployments behind the figures. */
export const AiModelConfigSchema = z.object({
  chat_model: z.string().nullable(),
  embedding_model: z.string().nullable(),
  chat_model_priced: z.boolean(),
  embedding_model_priced: z.boolean(),
});
export type AiModelConfig = z.infer<typeof AiModelConfigSchema>;

type AiCostSummaryBackend = z.infer<typeof AiCostSummaryBackendSchema>;

// ===========================================================================
// Hydration — backend shape → UI shape.
// ===========================================================================

const BACKEND_TO_UI_WORK_CLASS: Record<string, KnownWorkClassId> = {
  baseline: 'base-form',
  routing: 'ongoing',
  reeval: 'reeval',
  chat: 'chat',
  library_intake: 'library-intake',
  vendor_knowledge: 'vendor-knowledge',
};

/** The UI slot of a backend work class; an unknown class keeps its own id. */
export function uiWorkClassId(backend: string): WorkClassId {
  return BACKEND_TO_UI_WORK_CLASS[backend] ?? backend;
}

interface WorkClassPresentation {
  id: WorkClassId;
  name: string;
  label: string;
  tickets: string;
}

/**
 * The slots the engagement table always shows, in order, zero-filled when
 * a class has not run. Firm-scope classes are not among them: they have no
 * engagement and are shown in the firm budget card.
 */
const ENGAGEMENT_WORK_CLASS_SLOTS = [
  'base-form',
  'ongoing',
  'reeval',
  'chat',
  'narrative',
  'embed',
] as const satisfies readonly KnownWorkClassId[];

const WORK_CLASS_PRESENTATION: Record<KnownWorkClassId, WorkClassPresentation> = {
  'library-intake': {
    id: 'library-intake',
    name: 'Library intake',
    label: 'Per document',
    tickets: 'T-0147',
  },
  'vendor-knowledge': {
    id: 'vendor-knowledge',
    name: 'Vendor knowledge base',
    label: 'Per page or search',
    tickets: 'T-0193',
  },
  'base-form': {
    id: 'base-form',
    name: 'Base-formation pass',
    label: 'One-shot kickoff',
    tickets: 'T-0047',
  },
  ongoing: {
    id: 'ongoing',
    name: 'Ongoing routing',
    label: 'Per upload',
    tickets: 'T-0049',
  },
  reeval: {
    id: 'reeval',
    name: 'Re-evaluation on override',
    label: 'Per override',
    tickets: 'T-0051',
  },
  chat: {
    id: 'chat',
    name: 'Engagement chat',
    label: 'Per question',
    tickets: 'T-0053',
  },
  narrative: {
    id: 'narrative',
    name: 'Shared narrative drafting',
    label: 'Pulse / synth / exec',
    tickets: 'T-0061',
  },
  embed: {
    id: 'embed',
    name: 'Vector embedding',
    label: 'Per chunk',
    tickets: 'T-0043',
  },
};

/**
 * How a work class is shown. A class this screen has no entry for is
 * labelled from its own id ("some_new_class" reads "Some new class").
 */
export function workClassPresentation(id: WorkClassId): WorkClassPresentation {
  const known = (WORK_CLASS_PRESENTATION as Record<string, WorkClassPresentation | undefined>)[id];
  if (known) return known;
  const words = id.replace(/[_-]+/g, ' ').trim();
  return {
    id,
    name: words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Other',
    label: 'Other',
    tickets: '',
  };
}

/** The display name of a work class, by UI id or backend id. */
export function workClassName(id: string): string {
  return workClassPresentation(uiWorkClassId(id)).name;
}

function hydrateWorkClass(
  backend: AiCostSummaryBackend['by_work_class'],
  slots: readonly WorkClassId[] = ENGAGEMENT_WORK_CLASS_SLOTS,
): WorkClassBreakdown[] {
  const byId = new Map<WorkClassId, WorkClassBreakdown>();
  for (const row of backend) {
    const uiId = uiWorkClassId(row.work_class);
    const meta = workClassPresentation(uiId);
    byId.set(uiId, {
      id: uiId,
      name: meta.name,
      label: meta.label,
      tickets: meta.tickets,
      calls: row.calls,
      avg_tokens: row.avg_tokens,
      cost_per_call_cents: row.cost_per_call_cents,
      total_cents: row.total_cents,
    });
  }
  // Order matches WORK_CLASS_PRESENTATION's declaration order. Slots
  // without backend data render as zero rows so the table layout stays
  // stable across deployments where some work classes haven't run yet.
  const rows = slots.map((id) => {
    const existing = byId.get(id);
    if (existing) return existing;
    const meta = workClassPresentation(id);
    return {
      id: meta.id,
      name: meta.name,
      label: meta.label,
      tickets: meta.tickets,
      calls: 0,
      avg_tokens: 0,
      cost_per_call_cents: 0,
      total_cents: 0,
    };
  });
  // A class with no slot (new on the server) still gets its row.
  for (const [id, row] of byId) {
    if (!slots.includes(id)) rows.push(row);
  }
  return rows;
}

function hydrateFirmScope(backend: AiCostSummaryBackend['firm_scope']): FirmScopeSummary | null {
  if (!backend) return null;
  return {
    cap_cents: backend.cap_cents,
    is_default: backend.is_default,
    default_cap_cents: backend.default_cap_cents,
    used_cents: backend.used_cents,
    remaining_cents: backend.remaining_cents,
    percent_of_cap: backend.percent_of_cap,
    exhausted: backend.exhausted,
    txns: backend.txns,
    period_start: backend.period_start,
    work_classes: backend.work_classes,
    by_work_class: hydrateWorkClass(backend.by_work_class ?? [], []),
    recent_expensive_calls: (backend.recent_expensive_calls ?? []).map((r) => ({
      ts: r.ts,
      work_class: r.work_class,
      model_id: r.model_id ?? '—',
      estimated: r.estimated ?? false,
      tokens: r.tokens,
      cost_cents: r.cost_cents,
      ref: r.ref ?? '—',
    })),
  };
}

function hydrateEngagement(backend: AiCostSummaryBackend['by_engagement']): EngagementSpendRow[] {
  return backend.map((row) => ({
    engagement_id: row.engagement_id,
    client_name: row.client_name,
    engagement_name: row.engagement_name,
    cap_cents: row.cap_cents,
    cap_is_default: row.cap_is_default ?? false,
    period_start: row.period_start ?? null,
    used_cents: row.used_cents,
    txns: row.txns,
    alerts: row.alerts,
  }));
}

function hydrateRecentCall(
  row: AiCostSummaryBackend['recent_expensive_calls'][number],
): RecentCallRow {
  return {
    ts: row.ts,
    engagement_id: row.engagement_id,
    client_name: row.client_name,
    work_class: uiWorkClassId(row.work_class),
    tokens: row.tokens,
    cost_cents: row.cost_cents,
    model_id: row.model_id ?? '—',
    estimated: row.estimated ?? false,
    ref: row.ref ?? '—',
    user: row.user,
  };
}

/**
 * Derive billing-period bounds for the meter. v1 surfaces the current
 * calendar month — the backend aggregates spend over a "since this
 * month started" window so the day-elapsed marker aligns with the
 * meter's pro-rated tick.
 */
function deriveBillingPeriod(): {
  billing_period_start: string;
  billing_period_days_elapsed: number;
  billing_period_days_total: number;
} {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const nextMonthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const dayMs = 86_400_000;
  const daysElapsed = Math.max(1, Math.floor((now.getTime() - start.getTime()) / dayMs) + 1);
  const daysTotal = Math.round((nextMonthStart.getTime() - start.getTime()) / dayMs);
  return {
    billing_period_start: start.toISOString().slice(0, 10),
    billing_period_days_elapsed: Math.min(daysElapsed, daysTotal),
    billing_period_days_total: daysTotal,
  };
}

function hydrateSummary(backend: AiCostSummaryBackend): AiCostSummary {
  const trendSeries = backend.spend_trend_monthly.map((m) => m.total_cents);
  const lastIdx = trendSeries.length - 1;
  const mtdCents = trendSeries[lastIdx] ?? 0;
  const lastMonthCents = lastIdx > 0 ? (trendSeries[lastIdx - 1] ?? 0) : 0;
  const period = deriveBillingPeriod();
  return {
    firm_cap_cents: backend.firm_cap_cents,
    firm_mtd_cents: mtdCents,
    firm_last_month_cents: lastMonthCents,
    ...period,
    engagement_default_cap_cents: backend.engagement_default_cap_cents ?? null,
    estimated_models: backend.estimated_models ?? [],
    per_work_class: hydrateWorkClass(backend.by_work_class),
    per_engagement: hydrateEngagement(backend.by_engagement),
    recent_expensive_calls: backend.recent_expensive_calls.map(hydrateRecentCall),
    monthly_trend_cents: trendSeries,
    firm_scope: hydrateFirmScope(backend.firm_scope),
  };
}

// ===========================================================================
// Hook.
// ===========================================================================

export function getAiCostSummary(): Promise<AiCostSummary> {
  return apiClient('/api/v1/admin/ai-costs/summary', AiCostSummaryBackendSchema).then(
    hydrateSummary,
  );
}

export function useAiCostSummaryQuery() {
  return useQuery({
    queryKey: ['ai-costs', 'summary'] as const,
    queryFn: getAiCostSummary,
    staleTime: 60_000,
  });
}

export function getAiModelConfig(): Promise<AiModelConfig> {
  return apiClient('/api/v1/admin/ai-costs/model', AiModelConfigSchema);
}

export function useAiModelQuery() {
  return useQuery({
    queryKey: ['ai-costs', 'model'] as const,
    queryFn: getAiModelConfig,
    staleTime: 5 * 60_000,
  });
}

/**
 * "estimated at the fallback rate" for spend from models with no rate-card
 * row; what the cost page says next to a figure that is not exact.
 */
export const ESTIMATED_AT_FALLBACK_RATE = 'estimated at the fallback rate';

// ===========================================================================
// Firm budget (T-0147) — firm admin only.
// ===========================================================================

const FIRM_BUDGET_PATH = '/api/v1/admin/ai-costs/firm-budget';

/** The largest cap a firm admin may set: USD 100,000 a month (the server's typo guard). */
export const MAX_FIRM_CAP_CENTS = 10_000_000;

export function getFirmAiBudget(): Promise<FirmAiBudget> {
  return apiClient(FIRM_BUDGET_PATH, FirmAiBudgetSchema);
}

/** Set the firm monthly cap in cents; `null` goes back to the default. */
export function setFirmAiBudget(capCents: number | null): Promise<FirmAiBudget> {
  return apiClient(FIRM_BUDGET_PATH, FirmAiBudgetSchema, {
    method: 'PUT',
    body: { cap_cents: capCents },
    idempotencyKey:
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  });
}

export function useFirmAiBudgetQuery(enabled = true) {
  return useQuery({
    queryKey: ['ai-costs', 'firm-budget'] as const,
    queryFn: getFirmAiBudget,
    enabled,
    staleTime: 30_000,
  });
}

export function useSetFirmAiBudgetMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setFirmAiBudget,
    onSuccess: (budget) => {
      qc.setQueryData(['ai-costs', 'firm-budget'], budget);
      // The summary carries the same figures in `firm_scope`.
      void qc.invalidateQueries({ queryKey: ['ai-costs', 'summary'] });
    },
  });
}

/**
 * Read a typed amount of dollars as cents. Null when it is not a plain
 * amount ("50", "50.5", "1,250.00"); the cap must be zero or more and at
 * most `MAX_FIRM_CAP_CENTS`.
 */
export function parseCapDollars(input: string): number | null {
  const cleaned = input.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const cents = Math.round(Number(cleaned) * 100);
  return Number.isFinite(cents) && cents >= 0 && cents <= MAX_FIRM_CAP_CENTS ? cents : null;
}

// ===========================================================================
// Posture / formatting helpers (unchanged — re-exported for downstream
// callers).
// ===========================================================================

export type SpendPosture = 'healthy' | 'at-risk' | 'critical';

export function spendPosture(usedCents: number, capCents: number): SpendPosture {
  if (capCents <= 0) return 'healthy';
  const ratio = usedCents / capCents;
  if (ratio > 0.9) return 'critical';
  if (ratio > 0.75) return 'at-risk';
  return 'healthy';
}

export function formatCents(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatCentsAsK(cents: number): string {
  const dollars = cents / 100;
  return '$' + (dollars / 1000).toFixed(1) + 'K';
}
